import cloneDeep from 'lodash/cloneDeep'
import groupBy from 'lodash/groupBy'
import { mutateDateTime } from '../../../../utils/date.utils'
import { STATUS_PROGRESS, STATUS_PUNCTUALITY } from './status.constant'

const checkIsCurrentStatus = (status, activity) => {
	return (
		(activity.parent_status_id !== 0 &&
			status.activity_status_id === activity.parent_status_id) ||
		(activity.parent_status_id === 0 &&
			status.activity_status_id === activity.activity_status_id)
	)
}

const checkIsStatusInTimeline = (status, timelineGroupByStatusId) => {
	return (
		!!timelineGroupByStatusId[status.activity_status_id] &&
		Array.isArray(timelineGroupByStatusId[status.activity_status_id])
	)
}

const updateStatusPunctualityForCurrentStatus = ({
	status,
	index,
	activity,
	timelineGroupByStatusId,
}) => {
	const { activity_status_duration } = status
	if (activity_status_duration === 0)
		status.status_punctuality = STATUS_PUNCTUALITY.ON_TIME
	else {
		if (index === 0) {
			status.current_status_deadline = mutateDateTime(
				activity.activity_datetime_created,
				activity_status_duration,
				'hours'
			)
			status.status_punctuality = compareDates(
				activity.activity_datetime_created,
				new Date(),
				status.activity_status_duration
			)
		} else {
			const timelineStatus =
				timelineGroupByStatusId[status.activity_status_id].at(-1)
			status.current_status_deadline = mutateDateTime(
				timelineStatus.timeline_transaction_datetime,
				activity_status_duration,
				'hours'
			)
			status.status_punctuality = compareDates(
				timelineStatus.timeline_transaction_datetime,
				new Date(),
				status.activity_status_duration
			)
		}
	}
}

const findNextStatusFromTimeline = (
	status,
	statusGroupBySeqId,
	timelineGroupByStatusId
) => {
	const seqId = status.activity_status_sequence_id
	let iterator = 0
	let nextStatus = null
	while (iterator < Object.keys(statusGroupBySeqId).length) {
		if (statusGroupBySeqId[seqId + iterator + 1]) {
			const listOfStatuses = statusGroupBySeqId[seqId + iterator + 1]
			for (let i = 0; i < listOfStatuses.length; i++) {
				if (
					checkIsStatusInTimeline(listOfStatuses[i], timelineGroupByStatusId)
				) {
					nextStatus = listOfStatuses[i]
					break
				}
			}
		}
		if (!!nextStatus) break
		iterator += 1
	}
	return nextStatus
}

const compareDates = (prevDate, currentDate, duration) => {
	try {
		if (prevDate.getTime() + duration > currentDate.getTime()) {
			if (
				Math.abs(currentDate.getTime() - (prevDate.getTime() + duration)) <
				(10 * duration) / 100
			)
				return STATUS_PUNCTUALITY.SLIGHT_DELAY
			else return STATUS_PUNCTUALITY.DELAY
		}
		return STATUS_PUNCTUALITY.ON_TIME
	} catch (e) {
		console.error(e)
	}
}

const calculateStatusPunctuality = ({
	timelineStatus,
	nextTimelineStatus,
	status,
	index,
	activity,
	totalStatuses,
}) => {
	if (status.activity_status_duration === 0) {
		status.status_punctuality = STATUS_PUNCTUALITY.ON_TIME
	}

	if (index === 0) {
		if (status.activity_status_duration !== 0) {
			status.status_punctuality = compareDates(
				activity.activity_datetime_created,
				timelineStatus.timeline_transaction_datetime,
				status.activity_status_duration
			)
		}
		status.status_completed_time = timelineStatus.timeline_transaction_datetime
	} else if (index === totalStatuses - 1) {
		if (status.activity_status_duration !== 0) {
			status.status_punctuality = compareDates(
				timelineStatus.timeline_transaction_datetime,
				activity.activity_datetime_closed,
				status.activity_status_duration
			)
		}
		status.status_completed_time = activity.activity_datetime_closed
	} else {
		if (status.activity_status_duration !== 0) {
			status.status_punctuality = compareDates(
				timelineStatus.timeline_transaction_datetime,
				nextTimelineStatus.timeline_transaction_datetime,
				status.activity_status_duration
			)
		}
		status.status_completed_time =
			nextTimelineStatus.timeline_transaction_datetime
	}
}

const updateStatusPunctuality = (
	status,
	index,
	activity,
	timelineGroupByStatusId,
	statusGroupBySeqId,
	totalStatuses
) => {
	if (index === 0) {
		// If first status is not present in timeline, then activity created time will be considered as status completion time
		// Otherwise, timeline_transaction_datetime will be considered
		if (checkIsStatusInTimeline(status, timelineGroupByStatusId)) {
			const timelineStatus =
				timelineGroupByStatusId[status.activity_status_id].at(-1)
			calculateStatusPunctuality({
				timelineStatus,
				status,
				index,
				activity,
				totalStatuses,
			})
		} else {
			status.status_completed_time = activity.activity_datetime_created
			status.status_punctuality = STATUS_PUNCTUALITY.ON_TIME
		}
		status.status_progress = STATUS_PROGRESS.COMPLETED
	} else if (index === totalStatuses - 1) {
		if (checkIsStatusInTimeline(status, timelineGroupByStatusId)) {
			const timelineStatus =
				timelineGroupByStatusId[status.activity_status_id].at(-1)
			calculateStatusPunctuality({
				timelineStatus,
				status,
				index,
				activity,
				totalStatuses,
			})
		}
		status.status_progress = STATUS_PROGRESS.COMPLETED
	} else {
		if (checkIsStatusInTimeline(status, timelineGroupByStatusId)) {
			// We have to find next status in timeline, since if a timeline entry exists, that means that status has started
			// at that point of time. So, to get the duration of a status, we need to find when the status started
			// and when the status finished. Status finished can be found from next status entry in timeline.

			const nextStatus = findNextStatusFromTimeline(
				status,
				statusGroupBySeqId,
				timelineGroupByStatusId
			)
			if (!!nextStatus) {
				const timelineStatus =
					timelineGroupByStatusId[status.activity_status_id].at(-1)
				const nextTimelineStatus =
					timelineGroupByStatusId[nextStatus.activity_status_id].at(-1)

				calculateStatusPunctuality({
					timelineStatus,
					nextTimelineStatus,
					status,
					index,
					activity,
					totalStatuses,
				})
			}
			status.status_progress = STATUS_PROGRESS.COMPLETED
		} else {
			status.status_progress = STATUS_PROGRESS.SKIPPED
		}
	}
}

export const createStatusAndFormsList = (
	activity,
	statusFormsList,
	submittedForms,
	timelineStatus
) => {
	const newStatusFormsList = cloneDeep(statusFormsList)

	let isCurrentStatusFound = false

	// Creating a Map of submitted form ids
	const submittedFormIdMap = new Map()
	submittedForms.forEach(form => {
		submittedFormIdMap.set(form.data_form_id, form)
	})

	const timelineGroupByStatusId = groupBy(timelineStatus, 'activity_status_id')

	const statusGroupBySeqId = groupBy(
		statusFormsList,
		'activity_status_sequence_id'
	)

	newStatusFormsList.forEach((status, index) => {
		if (checkIsCurrentStatus(status, activity)) {
			updateStatusPunctualityForCurrentStatus({
				status,
				index,
				activity,
				timelineGroupByStatusId,
			})

			isCurrentStatusFound = true
			status.status_progress = STATUS_PROGRESS.CURRENT_STATUS
		} else if (!isCurrentStatusFound) {
			updateStatusPunctuality(
				status,
				index,
				activity,
				timelineGroupByStatusId,
				statusGroupBySeqId,
				newStatusFormsList.length
			)
		} else {
			status.status_progress = STATUS_PROGRESS.FUTURE
		}

		let formsSubmitted = 0

		status.forms.forEach(form => {
			if (submittedFormIdMap.has(form.form_id)) {
				const submittedForm = submittedFormIdMap.get(form.form_id)
				form.isSubmitted = true
				formsSubmitted += 1
				form.timeline_transaction_datetime =
					submittedForm.timeline_transaction_datetime
			} else form.isSubmitted = false
		})

		status.forms_submitted = formsSubmitted
	})
	return newStatusFormsList
}
